import React from 'react';
import { loadRemote } from '@module-federation/runtime';
import { MfWidgetRegistryContext, type WidgetRegistry } from '@brandclub/common-ui';

let validWidgetIds: Set<string> | null = null;
try {
  loadRemote<{ WIDGET_IDS: readonly string[] }>('concierge_widgets/Manifest')
    .then((mod) => {
      if (mod?.WIDGET_IDS) validWidgetIds = new Set(mod.WIDGET_IDS);
    })
    .catch(() => {});
} catch {}

function kebabToPascal(kebab: string): string {
  return kebab.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
}

const MF_RETRY_COUNT = 3;
const MF_RETRY_DELAY = 1500;

function loadWithRetry(pascalName: string, attempt = 1): Promise<{ default: React.ComponentType<any> }> {
  return loadRemote<{ default: React.ComponentType<any> }>(`concierge_widgets/${pascalName}`).then((mod) => {
    if (!mod) throw new Error(`Failed to load concierge_widgets/${pascalName}`);
    return mod;
  }).catch((err) => {
    if (attempt < MF_RETRY_COUNT) {
      return new Promise((resolve) => setTimeout(resolve, MF_RETRY_DELAY)).then(() =>
        loadWithRetry(pascalName, attempt + 1),
      );
    }
    throw err;
  });
}

const widgetCache = new Map<string, React.LazyExoticComponent<React.ComponentType<any>>>();

function lazyWidget(widgetId: string, pascalName: string) {
  return React.lazy(() =>
    loadWithRetry(pascalName).catch((err) => {
      widgetCache.delete(widgetId);
      throw err;
    }),
  );
}

const registry: WidgetRegistry = new Proxy({} as WidgetRegistry, {
  get(_target, prop: string) {
    if (typeof prop !== 'string') return undefined;
    if (validWidgetIds && !validWidgetIds.has(prop)) {
      console.warn(`[MfWidgetRegistry] Unknown widget ID "${prop}". Valid IDs:`, [...validWidgetIds]);
    }
    let cached = widgetCache.get(prop);
    if (!cached) {
      cached = lazyWidget(prop, kebabToPascal(prop));
      widgetCache.set(prop, cached);
    }
    return cached;
  },
  has(_target, prop: string) {
    if (validWidgetIds) return validWidgetIds.has(prop);
    return true;
  },
});

export function MfWidgetRegistryProvider({ children }: { children: React.ReactNode }) {
  return (
    <MfWidgetRegistryContext.Provider value={registry}>
      {children}
    </MfWidgetRegistryContext.Provider>
  );
}
